import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { toast } from "sonner";
import { Swords, BookOpen, CheckCircle, MessageSquare, Gift } from "lucide-react";
import ConfettiCelebration from "@/components/ConfettiCelebration";

/**
 * Listens for realtime challenge result notifications and shows a toast.
 * Mount once in AppShell.
 */
const ChallengeNotificationListener = () => {
  const { user } = useAuth();
  const [showReferralConfetti, setShowReferralConfetti] = useState(false);

  useEffect(() => {
    if (!user) return;

    const channel = supabase
      .channel("challenge-notifications")
      .on(
        "postgres_changes",
        {
          event: "INSERT",
          schema: "public",
          table: "notifications",
          filter: `user_id=eq.${user.id}`,
        },
        (payload) => {
          const n = payload.new as {
            id: string;
            title: string;
            message: string;
            type: string;
            metadata: Record<string, any>;
          };

          if (n.type === "homework_assigned") {
            toast(n.title, {
              description: n.message,
              duration: 10000,
              icon: <BookOpen className="w-5 h-5 text-primary" />,
              className: "border-primary/30 bg-primary/5",
            });
            supabase.from("notifications").update({ is_read: true }).eq("id", n.id).then();
            return;
          }

          if (n.type === "homework_graded") {
            const grade = n.metadata?.grade as string | undefined;
            const borderClass = grade === "correct" ? "border-success/30 bg-success/5" : grade === "partial" ? "border-amber-500/30 bg-amber-500/5" : "border-destructive/30 bg-destructive/5";
            toast(n.title, {
              description: n.message,
              duration: 12000,
              icon: <CheckCircle className="w-5 h-5 text-primary" />,
              className: borderClass,
            });
            supabase.from("notifications").update({ is_read: true }).eq("id", n.id).then();
            return;
          }

          if (n.type === "referral_reward") {
            setShowReferralConfetti(true);
            toast(n.title, {
              description: n.message,
              duration: 10000,
              icon: <Gift className="w-5 h-5 text-amber-500" />,
              className: "border-amber-500/30 bg-amber-500/5",
            });
            supabase.from("notifications").update({ is_read: true }).eq("id", n.id).then();
            return;
          }

          if (n.type === "homework_comment") {
            toast(n.title, {
              description: n.message,
              duration: 10000,
              icon: <MessageSquare className="w-5 h-5 text-primary" />,
              className: "border-primary/30 bg-primary/5",
            });
            supabase.from("notifications").update({ is_read: true }).eq("id", n.id).then();
            return;
          }

          if (n.type !== "challenge_result") return;

          const meta = n.metadata;
          const won =
            meta.challenger_percentage !== undefined &&
            meta.responder_percentage !== undefined &&
            meta.challenger_percentage >= meta.responder_percentage;

          toast(n.title, {
            description: n.message,
            duration: 12000,
            icon: <Swords className="w-5 h-5 text-primary" />,
            className: won
              ? "border-success/30 bg-success/5"
              : "border-destructive/30 bg-destructive/5",
            action: meta.link ? {
              label: "Zobacz porównanie",
              onClick: () => { window.location.href = meta.link; },
            } : undefined,
          });

          // Mark as read
          supabase
            .from("notifications")
            .update({ is_read: true })
            .eq("id", n.id)
            .then();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user]);

  return (
    <ConfettiCelebration
      isActive={showReferralConfetti}
      onComplete={() => setShowReferralConfetti(false)}
      message="Polecenie"
    />
  );
};

export default ChallengeNotificationListener;
